import { ChangeDetectionStrategy, Component, DestroyRef, inject, input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { LoginFacade } from '@pages/login/facades/login.facade';
import { Router, RouterLink } from '@angular/router';
import { asNonUndefined } from '@kaseya-utils';
import { ConfigurationFormGroup } from '@pages/login/models/login-configuration/configuration-form-group.interface';
import { LoginRecoveryPath } from '@pages/login-recovery-options/routes/login-recovery-path.enum';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import {
  InputComponent,
  inputValidators,
  LargeButtonComponent,
  LargeIconButtonComponent,
  LinkComponent,
  PrefetchLinkDirective,
} from '@kaseya-design-components';
import { LogInWithThirdPartySsoFacade } from '@pages/login/facades/log-in-with-third-party-sso.facade';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoginAttempt } from '@core/authorization/models/login-attempt.interface';
import { ToastService } from '@core/toast/toast.service';
import { LoginAttemptService } from '@core/authorization/services/login-attempt.service';
import { AuthorizationService } from '@core/authorization/services/authorization.service';

@Component({
  selector: 'itc-login-flow',
  templateUrl: 'login-flow.component.html',
  styleUrl: 'login-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    LargeButtonComponent,
    LargeIconButtonComponent,
    LinkComponent,
    TranslatePipe,
    RouterLink,
    PrefetchLinkDirective,
  ],
})
export class LoginFlowComponent {
  private readonly loginFacade = inject(LoginFacade);
  private readonly logInWithThirdPartySsoFacade = inject(LogInWithThirdPartySsoFacade);
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly loginAttemptService = inject(LoginAttemptService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly router = inject(Router);

  readonly uuid = input.required<string | undefined>();
  readonly configurationForm = input.required<FormGroup<ConfigurationFormGroup>>();
  readonly samlLoginUrl = this.loginFacade.samlLoginUrl;
  readonly LoginRecoveryPath = LoginRecoveryPath;
  readonly ApplicationPath = ApplicationPath;

  readonly password = new FormControl('', {
    nonNullable: true,
    validators: [inputValidators.required, inputValidators.notEmptyOrWhiteSpaces],
  });

  readonly loginConfiguration = this.loginFacade.loginConfiguration;
  readonly logInLoading = this.loginFacade.logInLoading;
  readonly checkMfaLoading = this.loginFacade.checkMfaLoading;
  readonly userInfoLoading = this.userInfoFacade.loading;

  constructor() {
    this.handleLogInCompleted();
    this.handleCheckMfaCompleted();
    this.handleMfaTokenExpiredError();
  }

  goToConfigurationStep(): void {
    this.loginFacade.goToLoginConfigurationStep();
  }

  logIn(): void {
    if (this.configurationForm().invalid || this.password.invalid) {
      this.password.markAsDirty();
      this.password.updateValueAndValidity();
      return;
    }

    this.loginFacade.logIn({
      organizationName: asNonUndefined(this.configurationForm().value.organizationName),
      username: asNonUndefined(this.configurationForm().value.username),
      password: asNonUndefined(this.password.value),
    });
  }

  logInWithThirdPartySSO(): void {
    this.logInWithThirdPartySsoFacade.logInWithThirdPartySso(asNonUndefined(this.samlLoginUrl()));
  }

  private handleLogInCompleted(): void {
    this.loginFacade.loginCompleted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const loginAttempt = this.loginFacade.loginAttempt();

      if (!loginAttempt) {
        return;
      }

      this.loginAttemptService.setLoginAttempt(loginAttempt);

      if (loginAttempt.allowOneStepLogin) {
        this.loginFacade.checkMfa(loginAttempt.mfaToken);
        return;
      }

      this.navigateToMfaPage(loginAttempt);
    });
  }

  private handleCheckMfaCompleted(): void {
    this.loginFacade.checkMfaCompleted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const authorizationData = this.loginFacade.authorizationData();

      if (!authorizationData) {
        return;
      }

      this.authorizationService.logIn(authorizationData);
    });
  }

  private navigateToMfaPage({ enrolled2Fa, hasTrustedDevices }: LoginAttempt): void {
    if (!enrolled2Fa) {
      this.router.navigate([ApplicationPath.TotpMfaEnrollment]);
      return;
    }

    if (hasTrustedDevices) {
      this.router.navigate([ApplicationPath.PushMfaVerification]);
      return;
    }

    this.router.navigate([ApplicationPath.TotpMfaVerification]);
  }

  private handleMfaTokenExpiredError(): void {
    this.loginFacade.mfaTokenExpiredError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.toastService.showErrorToast('login.messages.mfa-token-is-expired');
      this.configurationForm().reset();
    });
  }
}
